import React from 'react';
import { useNavigate } from 'react-router-dom';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import ViewModuleRoundedIcon from '@mui/icons-material/ViewModuleRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useOptionalAuth } from 'contexts/AuthContext';

import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import {
  ArticleSummary,
  ArticleVisibility,
  changeArticleVisibility,
  deleteArticle,
  listArticle,
} from 'stores/ArticleStoreAdapter';
import { LabelResult, listLabel } from 'stores/LabelStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import ArticleCard from './components/ArticleCard';
import ArticleEmptyState from './components/ArticleEmptyState';
import ArticleGroupList, { GroupMode } from './components/ArticleGroupList';
import ArticleListItem from './components/ArticleListItem';
import ArticleSearchBar, { ArticleSearchState } from './components/ArticleSearchBar';

/** ログイン時のみ末尾に固定表示する既存の4記事（静的埋め込み。API の戻り値に関係なく常に末尾） */
type FixedDoc = {
  id: string;
  title: string;
  description: string;
  author: string;
  date: string;
  path: string;
  tags: string[];
  readTime: string;
};

const FIXED_DOCS: FixedDoc[] = [
  {
    id: '2',
    title: 'AIと作る、壊れないバックエンド',
    description:
      'バイブコーディングで認知負債を積まないための実践ガイド。設計書駆動でAIを制御し、要件定義からE2Eテスト完了までトレーサブルに開発する全15記事シリーズ。',
    author: 'Hiroki Chigira',
    date: '2026-08-02',
    path: '/devdocs/workflow',
    tags: ['AI', 'Workflow', 'Kiro', 'Backend'],
    readTime: '180 min read',
  },
  {
    id: '3',
    title: 'AIと作る、壊れないバックエンド - 実践編',
    description:
      'チームTODO管理アプリを題材に、環境構築から要件定義・バックエンド実装・フロントエンド開発までを一気通貫で体験するハンズオン。全16記事で、生成AIを使った開発が初めてでも手を動かしながらAI駆動開発の全工程を習得できます。',
    author: 'Hiroki Chigira',
    date: '2026-08-16',
    path: '/devdocs/handson',
    tags: ['AI', 'Hands-on', 'Kiro', 'Spring Boot', 'React'],
    readTime: '360 min read',
  },
  {
    id: '1',
    title: 'AI活用による開発生産性向上の実績分析（バックエンド）',
    description:
      'Comparing manual development vs AI-assisted development effort for Kanzhong backend (Java / Spring Boot). Efficiency analysis based on function point estimation using simplified IFPUG methodology.',
    author: 'Hiroki Chigira',
    date: '2026-08-02',
    path: '/devdocs/productivity-analysis',
    tags: ['AI', 'Productivity', 'Backend'],
    readTime: '5 min read',
  },
];

const PAGE_SIZE = 10;

type ViewMode = 'all' | 'author' | 'label';

const TAB_OPTIONS: { value: ViewMode; label: string }[] = [
  { value: 'all', label: '全記事' },
  { value: 'author', label: 'Author ごと' },
  { value: 'label', label: 'Labels ごと' },
];

/**
 * Featured セクションの静的カード（既存記事への導線）。
 * 記事カード（ArticleCard）と見た目を揃える（角丸2 / p:2 / ホバー上端アクセント / caption メタ）。
 */
const FeaturedCard: React.FC<{ doc: FixedDoc; onOpen: () => void }> = ({ doc, onOpen }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      position: 'relative',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      overflow: 'hidden',
      transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        bgcolor: 'primary.main',
        transform: 'scaleX(0)',
        transformOrigin: 'left',
        transition: 'transform 0.25s ease',
      },
      '&:hover': {
        borderColor: 'primary.main',
        transform: 'translateY(-2px)',
        boxShadow: (theme) => `0 6px 20px ${theme.palette.action.hover}`,
      },
      '&:hover::before': { transform: 'scaleX(1)' },
    }}
  >
    <CardActionArea
      onClick={onOpen}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        '& .MuiCardActionArea-focusHighlight': { opacity: 0 },
      }}
    >
      <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
        {doc.tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, borderRadius: 1 }}
          />
        ))}
      </Stack>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{
          mb: 0.75,
          lineHeight: 1.35,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {doc.title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 1.5,
          flex: 1,
          lineHeight: 1.6,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {doc.description}
      </Typography>
      <Box sx={{ width: '100%', mt: 'auto' }}>
        <Divider sx={{ mb: 1 }} />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PersonOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              {doc.author}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              {doc.readTime}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ ml: 'auto !important', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {doc.date}
            <ArrowForwardIcon sx={{ fontSize: 13 }} />
          </Typography>
        </Stack>
      </Box>
    </CardActionArea>
  </Card>
);

const DevDocsHome: React.FC = () => {
  const navigate = useNavigate();
  const { userSession, users } = useOptionalAuth();
  const isLoggedIn = userSession !== null;

  const [labels, setLabels] = React.useState<LabelResult[]>([]);
  const [search, setSearch] = React.useState<ArticleSearchState>({
    keyword: '',
    labelIds: [],
    authorUserIds: [],
  });
  const [viewMode, setViewMode] = React.useState<ViewMode>('all');
  /** 全記事モードの表示レイアウト（カード / リスト） */
  const [layout, setLayout] = React.useState<'card' | 'list'>('card');

  const [articles, setArticles] = React.useState<ArticleSummary[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  // 削除確認
  const [deleteTarget, setDeleteTarget] = React.useState<ArticleSummary | null>(null);

  // ラベル候補取得。初回ロードとヒーローの更新ボタンで共用。
  const fetchLabels = React.useCallback(async () => {
    try {
      const list = await listLabel();
      setLabels(list);
    } catch {
      setLabels([]);
    }
  }, []);

  React.useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const grouping = viewMode !== 'all';
  const hasSearchCondition =
    search.keyword.trim() !== '' || search.labelIds.length > 0 || search.authorUserIds.length > 0;

  // 記事取得。全記事モードは page/pageSize、グループモードは grouping=true で全件。
  const fetchArticles = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await listArticle({
        keyword: search.keyword.trim() || undefined,
        labelIds: search.labelIds.length ? search.labelIds : undefined,
        authorUserIds: search.authorUserIds.length ? search.authorUserIds : undefined,
        grouping,
        page: grouping ? undefined : page,
        pageSize: grouping ? undefined : PAGE_SIZE,
      });
      setArticles(res.articles);
      setTotalCount(res.totalCount ?? res.articles.length);
    } catch (e) {
      errorDefault('記事の取得に失敗しました');
      // eslint-disable-next-line no-console
      console.error(e);
      setArticles([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [search, grouping, page]);

  React.useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // ヒーローの更新ボタン: 記事一覧とラベル候補をまとめて再取得する（ゲストも利用可）
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchArticles(), fetchLabels()]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchArticles, fetchLabels]);

  // 検索条件・表示モード変更時は 1 ページ目に戻す
  const handleSearchChange = (next: ArticleSearchState) => {
    setSearch(next);
    setPage(1);
  };
  const handleViewModeChange = (_: React.SyntheticEvent, next: ViewMode) => {
    setViewMode(next);
    setPage(1);
  };
  const clearSearch = () => {
    setSearch({ keyword: '', labelIds: [], authorUserIds: [] });
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // --- editable 記事の操作 ---
  const handleOpen = (articleId: string) => navigate(`/devdocs/article/${articleId}`);
  const handleEdit = (articleId: string) => navigate(`/devdocs/article/${articleId}/edit`);

  const handleChangeVisibility = async (article: ArticleSummary, visibility: ArticleVisibility) => {
    try {
      await changeArticleVisibility({ articleId: article.articleId, visibility });
      successDefault('公開設定を変更しました');
      setArticles((prev) =>
        prev.map((a) => (a.articleId === article.articleId ? { ...a, visibility } : a)),
      );
    } catch (e) {
      errorDefault('公開設定の変更に失敗しました', toErrorDetail(e));
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteArticle({ articleId: deleteTarget.articleId });
      successDefault('記事を削除しました');
      setDeleteTarget(null);
      fetchArticles();
    } catch (e) {
      errorDefault('記事の削除に失敗しました', toErrorDetail(e));
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  // editable 記事のみ操作ハンドラを渡す（内部でも article.editable を見るが、ゲストには一切渡さない）
  const cardHandlers = isLoggedIn
    ? {
        onEdit: handleEdit,
        onDelete: (a: ArticleSummary) => setDeleteTarget(a),
        onChangeVisibility: handleChangeVisibility,
      }
    : {};

  // 全記事モードの空表示（検索中かどうかで文言・アクションを出し分け）
  const renderEmpty = () =>
    hasSearchCondition ? (
      <ArticleEmptyState
        icon={<SearchOffIcon />}
        title="条件に合う記事が見つかりませんでした"
        description="キーワードやラベル・著者の絞り込みを変更するか、条件をクリアしてみてください。"
        actionLabel="検索条件をクリア"
        onAction={clearSearch}
      />
    ) : (
      <ArticleEmptyState
        icon={<ArticleOutlinedIcon />}
        title="まだ公開された記事がありません"
        description={
          isLoggedIn
            ? 'ワークスペースで作成した md / knz ファイルを記事として公開できます。'
            : 'ログインすると、あなたも記事を公開できます。'
        }
        actionLabel={isLoggedIn ? '記事を公開' : undefined}
        actionIcon={isLoggedIn ? <AddIcon /> : undefined}
        onAction={isLoggedIn ? () => navigate('/devdocs/article/create') : undefined}
      />
    );

  return (
    <Stack spacing={3} sx={{ maxWidth: 1080, mr: 'auto' }}>
      {/* ヒーローセクション（共通 DomainHero・DevInsights パレット） */}
      {/* 他ドメインと同様、ホームに戻るはヒーロー内に表示（ログイン時のみ・ゲストは不可） */}
      <DomainHero
        eyebrow="The Engineering Journal"
        title="Dev Insights"
        tagline="Field notes from the engineering team — technical deep dives, productivity analyses, and the architecture decisions behind what we build."
        palette={HERO_PALETTES.devdocs}
        showHome={isLoggedIn}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        actions={
          isLoggedIn ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/devdocs/article/create')}
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 2,
                py: 0.75,
                boxShadow: 'none',
                bgcolor: '#fff',
                color: '#0f172a',
                '& .MuiButton-startIcon': { color: '#0f172a' },
                '&:hover': { boxShadow: 'none', bgcolor: '#e8eefc' },
              }}
            >
              新規記事
            </Button>
          ) : undefined
        }
      />

      {/* 検索バー */}
      <ArticleSearchBar
        value={search}
        onChange={handleSearchChange}
        labels={labels}
        users={users}
      />

      {/* 表示モード切替 + 件数 */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs
          value={viewMode}
          onChange={handleViewModeChange}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 44,
              minWidth: 'auto',
              px: 2,
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
          }}
        >
          {TAB_OPTIONS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 0.5 }}>
          {viewMode === 'all' && !loading && articles.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              全 {totalCount} 件
            </Typography>
          )}
          {/* カード / リスト 表示切替（全記事・グループ表示 共通で引き継ぐ） */}
          {!loading && articles.length > 0 && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={layout}
              onChange={(_, v: 'card' | 'list' | null) => v && setLayout(v)}
              sx={{
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 1.5,
                  px: 0.75,
                  py: 0.25,
                  color: 'text.disabled',
                  '&.Mui-selected': {
                    color: 'primary.main',
                    bgcolor: 'action.selected',
                    '&:hover': { bgcolor: 'action.selected' },
                  },
                },
              }}
            >
              <ToggleButton value="card">
                <Tooltip title="カード表示">
                  <ViewModuleRoundedIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="list">
                <Tooltip title="一覧表示">
                  <ViewListRoundedIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      </Stack>

      {/* コンテンツ */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : viewMode === 'all' ? (
        <Stack spacing={2.5}>
          {articles.length === 0 ? (
            renderEmpty()
          ) : (
            <>
              {layout === 'card' ? (
                <Grid container spacing={2.5}>
                  {articles.map((article) => (
                    <Grid key={article.articleId} size={{ xs: 12, md: 6 }}>
                      <ArticleCard article={article} onOpen={handleOpen} {...cardHandlers} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Stack spacing={1}>
                  {articles.map((article) => (
                    <ArticleListItem
                      key={article.articleId}
                      article={article}
                      onOpen={handleOpen}
                      {...cardHandlers}
                    />
                  ))}
                </Stack>
              )}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          )}
        </Stack>
      ) : (
        <ArticleGroupList
          mode={viewMode as GroupMode}
          layout={layout}
          articles={articles}
          onOpen={handleOpen}
          {...cardHandlers}
        />
      )}

      {/* 末尾固定4記事（ログイン時のみ） */}
      {isLoggedIn && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <StarBorderRoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Featured
            </Typography>
            <Typography variant="caption" color="text.secondary">
              おすすめの読み物
            </Typography>
          </Stack>
          <Grid container spacing={2.5}>
            {FIXED_DOCS.map((doc) => (
              <Grid key={doc.id} size={{ xs: 12, md: 6 }}>
                <FeaturedCard doc={doc} onOpen={() => navigate(doc.path)} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      {/* 削除確認ダイアログ */}
      <DeleteConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="記事の削除"
        confirmLabel="削除"
      >
        <Typography variant="body2">
          「{deleteTarget?.title}」を削除します。この操作は取り消せません。
        </Typography>
      </DeleteConfirmDialog>
    </Stack>
  );
};

export default DevDocsHome;
